import './MetadataTable.css';
import { createLogger } from '../../utils/logger';

const logger = createLogger('MetadataTable');

interface MetadataTableProps {
  data: Record<string, any> | null | undefined;
  className?: string;
}

/**
 * Generic metadata table component that renders key-value pairs
 * Handles formatting of common data types (dates, numbers, objects, etc.)
 * Skips binary/buffer fields and empty values
 */
export default function MetadataTable({ data, className = '' }: MetadataTableProps) {
  if (!data || Object.keys(data).length === 0) {
    return (
      <div className={`metadata-table-empty ${className}`}>
        No metadata available
      </div>
    );
  }

  // Format field name: convert camelCase/PascalCase to readable format
  // Examples: "Make" -> "Make", "ISOSpeedRatings" -> "ISO Speed Ratings", "GPSAltitude" -> "GPS Altitude"
  const formatFieldName = (key: string): string => {
    return key
      .replace(/([A-Z])/g, ' $1') // Add space before capitals
      .replace(/^./, (str) => str.toUpperCase()) // Capitalize first letter
      .trim();
  };

  // Format value based on type for display
  const formatValue = (value: any): string => {
    if (value === null || value === undefined) {
      return '—';
    }

    // Skip ArrayBuffers (from browser File API)
    if (value instanceof ArrayBuffer) {
      return '[Binary Data]';
    }

    // Handle Date objects
    if (value instanceof Date) {
      return value.toLocaleString();
    }

    // Handle numbers - limit decimal places
    if (typeof value === 'number') {
      // For very small decimals (like GPS precision), show 6 decimals
      if (value !== Math.floor(value)) {
        return value.toFixed(6).replace(/\.?0+$/, '');
      }
      return value.toString();
    }

    // Handle booleans
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }

    // Handle arrays
    if (Array.isArray(value)) {
      if (value.length === 0) return '[]';
      // Join array elements, limit display
      const joined = value.slice(0, 5).join(', ');
      return value.length > 5 ? `${joined}...` : joined;
    }

    // Handle objects - convert to JSON string
    if (typeof value === 'object') {
      try {
        const jsonStr = JSON.stringify(value);
        return jsonStr.length > 50 ? `${jsonStr.substring(0, 47)}...` : jsonStr;
      } catch {
        return '[Object]';
      }
    }

    // Default: string representation
    return String(value);
  };

  // Filter and sort entries: skip empty values and binary data
  const entries = Object.entries(data)
    .filter(([_key, value]) => {
      // Skip ArrayBuffers, empty strings, null
      if (value instanceof ArrayBuffer) return false;
      if (value === null || value === undefined) return false;
      if (typeof value === 'string' && value.trim() === '') return false;
      return true;
    })
    .sort(([keyA], [keyB]) => keyA.localeCompare(keyB)); // Sort alphabetically

  if (entries.length === 0) {
    return (
      <div className={`metadata-table-empty ${className}`}>
        No metadata available
      </div>
    );
  }

  return (
    <div className={`metadata-table ${className}`}>
      <table className="metadata-table-content">
        <tbody>
          {entries.map(([key, value]) => (
            <tr key={key} className="metadata-table-row">
              <td className="metadata-table-key">{formatFieldName(key)}</td>
              <td className="metadata-table-value">{formatValue(value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
